/* eslint-disable max-lines */
import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal } from 'lucide-react'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Person, ProfileMembership } from '@/lib/contacts/types'
import { displayDate } from '@/lib/dates'
import { AssignMembershipDialog } from '@/pages/AssignMembershipDialog'
import { ChangeMembershipLevelDialog } from '@/pages/ChangeMembershipLevelDialog'
import {
  CorrectMembershipDatesDialog,
  RemoveMembershipDialog,
} from '@/pages/MembershipCorrectionDialogs'
import {
  ApproveMembershipDialog,
  EndMembershipDialog,
  RejectMembershipDialog,
  RenewMembershipDialog,
} from '@/pages/PersonMembershipDialogs'
import { isNeverRenewalDue, membershipRenewalDueLabel } from '@/pages/PersonMembershipDisplay'
import { MembershipHistory } from '@/pages/PersonMembershipHistory'
import { PersonEventHistory } from '@/pages/PersonEventHistory'
import { PersonMembershipAutopay } from '@/pages/PersonMembershipAutopay'
import { PersonMembershipEmpty } from '@/pages/PersonMembershipEmpty'
import { ProfileField } from '@/pages/ProfileField'
import { ReactivateMembershipDialog } from '@/pages/ReactivateMembershipDialog'
import { useViewOnly } from '@/components/templates/app-shell'
import { isConnectionOwned, OwnedBanner, OwnedChip } from '@/components/OwnedChip'

type MembershipDialog =
  | 'assign'
  | 'renew'
  | 'correct-dates'
  | 'change-level'
  | 'lapse'
  | 'suspend'
  | 'reactivate'
  | 'approve'
  | 'reject'
  | 'remove'

const membershipStatusVariant: Record<ProfileMembership['status'], BadgeProps['variant']> = {
  Active: 'success',
  Lapsed: 'warning',
  Suspended: 'neutral',
  'Pending-New': 'warning',
  'Pending-Renewal': 'warning',
}

export function PersonMembershipTab({
  person,
  contactId,
  currentAssociationDate,
  currentAssociationTimeZone,
}: {
  person: Person
  contactId: number
  currentAssociationDate?: string
  currentAssociationTimeZone?: string
}) {
  const queryClient = useQueryClient()
  const viewOnly = useViewOnly()
  const [dialog, setDialog] = React.useState<MembershipDialog | null>(null)

  async function handleMembershipChanged() {
    setDialog(null)
    await queryClient.invalidateQueries({ queryKey: ['person', contactId] })
    await queryClient.invalidateQueries({ queryKey: ['membership-levels'] })
  }

  return (
    <>
      <MembershipSection
        person={person}
        currentAssociationTimeZone={currentAssociationTimeZone}
        canManage={!viewOnly}
        onAssign={() => setDialog('assign')}
        onRenew={() => setDialog('renew')}
        onCorrectDates={() => setDialog('correct-dates')}
        onChangeLevel={() => setDialog('change-level')}
        onLapse={() => setDialog('lapse')}
        onSuspend={() => setDialog('suspend')}
        onReactivate={() => setDialog('reactivate')}
        onApprove={() => setDialog('approve')}
        onReject={() => setDialog('reject')}
        onRemove={() => setDialog('remove')}
      />
      <MembershipDialogHost
        dialog={dialog}
        person={person}
        currentAssociationDate={currentAssociationDate}
        onClose={() => setDialog(null)}
        onChanged={handleMembershipChanged}
      />
    </>
  )
}

function MembershipDialogHost({
  dialog,
  person,
  currentAssociationDate,
  onClose,
  onChanged,
}: {
  dialog: MembershipDialog | null
  person: Person
  currentAssociationDate?: string
  onClose: () => void
  onChanged: () => Promise<void>
}) {
  if (dialog === 'assign') {
    return (
      <AssignMembershipDialog
        person={person}
        defaultMemberSinceOn={currentAssociationDate}
        onClose={onClose}
        onAssigned={onChanged}
      />
    )
  }

  if (!person.membership) return null

  if (dialog === 'lapse' || dialog === 'suspend') {
    return (
      <EndMembershipDialog
        action={dialog}
        person={person}
        onClose={onClose}
        onChanged={onChanged}
      />
    )
  }

  switch (dialog) {
    case 'renew':
      return (
        <RenewMembershipDialog
          person={person}
          membership={person.membership}
          onClose={onClose}
          onRenewed={onChanged}
        />
      )
    case 'correct-dates':
      return (
        <CorrectMembershipDatesDialog
          person={person}
          membership={person.membership}
          onClose={onClose}
          onCorrected={onChanged}
        />
      )
    case 'change-level':
      return (
        <ChangeMembershipLevelDialog
          person={person}
          membership={person.membership}
          onClose={onClose}
          onChanged={onChanged}
        />
      )
    case 'reactivate':
      return (
        <ReactivateMembershipDialog
          person={person}
          membership={person.membership}
          onClose={onClose}
          onReactivated={onChanged}
        />
      )
    case 'approve':
      return (
        <ApproveMembershipDialog
          person={person}
          membership={person.membership}
          onClose={onClose}
          onApproved={onChanged}
        />
      )
    case 'reject':
      return <RejectMembershipDialog person={person} onClose={onClose} onRejected={onChanged} />
    case 'remove':
      return <RemoveMembershipDialog person={person} onClose={onClose} onRemoved={onChanged} />
    default:
      return null
  }
}

// eslint-disable-next-line max-lines-per-function
function MembershipSection({
  person,
  currentAssociationTimeZone,
  canManage,
  onAssign,
  onRenew,
  onCorrectDates,
  onChangeLevel,
  onLapse,
  onSuspend,
  onReactivate,
  onApprove,
  onReject,
  onRemove,
}: {
  person: Person
  currentAssociationTimeZone?: string
  canManage: boolean
  onAssign: () => void
  onRenew: () => void
  onCorrectDates: () => void
  onChangeLevel: () => void
  onLapse: () => void
  onSuspend: () => void
  onReactivate: () => void
  onApprove: () => void
  onReject: () => void
  onRemove: () => void
}) {
  if (!person.membership) {
    return (
      <PersonMembershipEmpty
        person={person}
        currentAssociationTimeZone={currentAssociationTimeZone}
        onAssign={onAssign}
        canAssign={canManage}
      />
    )
  }

  const canManageMembership = canManage && !isConnectionOwned(person.membership.owner)
  const canRenew =
    canManageMembership &&
    person.membership.status === 'Active' &&
    person.membership.next_renewal_due_on &&
    !isNeverRenewalDue(person.membership.renewal_due_on)
  const canEnd = person.membership.status === 'Active'
  const canReactivate =
    canManageMembership && ['Lapsed', 'Suspended'].includes(person.membership.status)
  const canResolveApplication = canManageMembership && person.membership.status === 'Pending-New'

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <OwnedBanner
        owner={person.membership.owner}
        externalUrl={person.membership.external_url}
        lastSyncedAt={person.membership.last_synced_at}
      />
      <Card>
        <CardHeader className="gap-3 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-body font-semibold text-foreground">
                  {person.membership.level}
                </CardTitle>
                <Badge variant={membershipStatusVariant[person.membership.status]}>
                  {person.membership.status}
                </Badge>
                <OwnedChip owner={person.membership.owner} />
              </div>
              <p className="pt-1 text-body-sm text-muted-foreground">
                {membershipRenewalSummary(person.membership)}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {canRenew && (
                <Button type="button" size="sm" onClick={onRenew}>
                  Renew
                </Button>
              )}
              {canReactivate && (
                <Button type="button" size="sm" onClick={onReactivate}>
                  Reactivate
                </Button>
              )}
              {canResolveApplication && (
                <Button type="button" size="sm" onClick={onApprove}>
                  Approve
                </Button>
              )}
              {canManageMembership && (
                <MembershipManageMenu
                  canEnd={canEnd}
                  canResolveApplication={canResolveApplication}
                  onCorrectDates={onCorrectDates}
                  onChangeLevel={onChangeLevel}
                  onLapse={onLapse}
                  onSuspend={onSuspend}
                  onReject={onReject}
                  onRemove={onRemove}
                />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="divide-y divide-border p-0">
          <section className="px-4 pb-4">
            <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
              <ProfileField label="Member number" value={memberNumberLabel(person.membership)} />
              <ProfileField
                label="Member since"
                value={displayDate(person.membership.member_since_on)}
              />
              <ProfileField
                label="Renewal due"
                value={membershipRenewalDueLabel(person.membership.renewal_due_on)}
              />
              <ScheduledEndField membership={person.membership} />
            </dl>
          </section>
          {person.membership.autopay && (
            <PersonMembershipAutopay autopay={person.membership.autopay} contactId={person.id} />
          )}
          <MembershipActivity person={person} timeZone={currentAssociationTimeZone} />
        </CardContent>
      </Card>
    </div>
  )
}

function ScheduledEndField({ membership }: { membership: ProfileMembership }) {
  if (!membership.scheduled_end_on) return null

  return (
    <ProfileField
      label="Scheduled end"
      value={`Cancels on ${displayDate(membership.scheduled_end_on)}`}
    />
  )
}

function MembershipActivity({ person, timeZone }: { person: Person; timeZone?: string }) {
  return (
    <>
      <MembershipHistory
        history={person.membership_history}
        autopayHistory={person.autopay_history ?? []}
        timeZone={timeZone}
      />
      <PersonEventHistory history={person.event_history ?? []} timeZone={timeZone} />
    </>
  )
}

function MembershipManageMenu({
  canEnd,
  canResolveApplication,
  onCorrectDates,
  onChangeLevel,
  onLapse,
  onSuspend,
  onReject,
  onRemove,
}: {
  canEnd: boolean
  canResolveApplication: boolean
  onCorrectDates: () => void
  onChangeLevel: () => void
  onLapse: () => void
  onSuspend: () => void
  onReject: () => void
  onRemove: () => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-2">
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
          Manage membership
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {canResolveApplication ? (
          <>
            <DropdownMenuLabel>Application</DropdownMenuLabel>
            <DropdownMenuItem destructive onSelect={onReject}>
              Reject
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuLabel>Change</DropdownMenuLabel>
            {canEnd && <DropdownMenuItem onSelect={onChangeLevel}>Change level</DropdownMenuItem>}
            <DropdownMenuItem onSelect={onCorrectDates}>Correct dates</DropdownMenuItem>
          </>
        )}
        {canEnd && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Status</DropdownMenuLabel>
            <DropdownMenuItem onSelect={onLapse}>Lapse</DropdownMenuItem>
            <DropdownMenuItem destructive onSelect={onSuspend}>
              Suspend
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Danger</DropdownMenuLabel>
            <DropdownMenuItem destructive onSelect={onRemove}>
              Delete membership (created in error)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function memberNumberLabel(membership: ProfileMembership) {
  return membership.member_number ? `#${membership.member_number}` : '-'
}

function membershipRenewalSummary(membership: ProfileMembership) {
  if (membership.status === 'Pending-New') return 'awaiting approval'

  const renewalDue = membershipRenewalDueLabel(membership.renewal_due_on)

  return renewalDue === 'Never' ? 'never expires' : `renews ${renewalDue}`
}
